"use client"

import { useState, type MouseEvent } from "react"
import Link from "next/link"
import { motion, useReducedMotion } from "motion/react"
import {
  Check,
  Shield,
  RotateCcw,
  CreditCard,
  Crown,
  Loader2,
  ArrowDown,
} from "lucide-react"

import { analytics, trackEvent } from "@/lib/analytics"
import { IDENTIFY_CTA, identifyCtaProps } from "@/lib/identify-cta"
import { Reveal, Stagger, HoverLift } from "@/components/motion"
import { EASE_OUT_QUINT } from "@/lib/motion-presets"
import { useAuth } from "@/contexts/AuthContext"
import {
  navigateToResolvedUrl,
  openModifiedClickTarget,
} from "@/lib/navigation/modified-click"
import { PREMIUM_TRIAL_DAYS } from "@/lib/auth/feature-flags"
import { WEB_DISCOUNT, WEB_DISCOUNT_REASON } from "@/lib/pricing-display"
import { PremiumTrialTimeline } from "@/components/billing/premium-trial-timeline"

// This card's Premium CTA opens Stripe checkout (not the App Store), so it
// quotes the web price. See lib/pricing-display.ts.
const MONTHLY_PRICE = WEB_DISCOUNT.monthly.web
const YEARLY_PRICE = WEB_DISCOUNT.yearly.web
const YEARLY_SAVINGS = MONTHLY_PRICE * 12 - YEARLY_PRICE

type BillingInterval = "monthly" | "yearly"

const FREE_FEATURES = [
  "3 grass IDs + 3 shared weed IDs or diagnoses per month",
  "1 care plan per month",
  "Unlimited saved lawns",
  "Care plan PDF downloads",
  "Free calculators & guides",
] as const

const PREMIUM_FEATURES = [
  { text: "Smart reminders timed to your zip & grass", highlight: true },
  { text: "Unlimited personalized care plans", highlight: true },
  { text: "Unlimited grass IDs, weed IDs, and lawn diagnoses", highlight: true },
  { text: "Full lawn health and analysis history", highlight: true },
  { text: "Saved soil tests + re-test reminders", highlight: false },
] as const

function PopularBadge() {
  const reduce = useReducedMotion()
  return (
    <motion.div
      className="absolute -top-3.5 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10"
      initial={reduce ? false : { opacity: 0, y: -4 }}
      whileInView={reduce ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.3, ease: EASE_OUT_QUINT, delay: 0.18 }}
    >
      <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-linear-to-r from-green-600 to-emerald-600 text-white shadow-md">
        <Crown className="w-3 h-3" aria-hidden="true" />
        Most popular
      </span>
    </motion.div>
  )
}

function TrustBand() {
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 mb-3 text-sm text-gray-600">
      <span className="font-semibold text-gray-900">Plans for all 50 states</span>
      <span aria-hidden="true" className="text-gray-300">
        ·
      </span>
      <span>Cancel anytime</span>
    </div>
  )
}

function BillingToggle({
  interval,
  onChange,
}: {
  interval: BillingInterval
  onChange: (next: BillingInterval) => void
}) {
  return (
    <div className="flex justify-center mb-8 md:mb-10">
      <div
        role="tablist"
        aria-label="Billing interval"
        className="bg-white border border-gray-200 p-1 rounded-full inline-flex shadow-sm"
      >
        <button
          type="button"
          role="tab"
          aria-selected={interval === "monthly"}
          onClick={() => onChange("monthly")}
          className={`px-4 sm:px-5 py-2 rounded-full text-sm font-semibold transition-colors min-h-[44px] ${
            interval === "monthly"
              ? "bg-gray-900 text-white shadow-sm"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          Monthly
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={interval === "yearly"}
          onClick={() => onChange("yearly")}
          className={`px-4 sm:px-5 py-2 rounded-full text-sm font-semibold transition-colors min-h-[44px] inline-flex items-center gap-2 ${
            interval === "yearly"
              ? "bg-gray-900 text-white shadow-sm"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          Yearly
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold ${
              interval === "yearly"
                ? "bg-green-400/20 text-green-200"
                : "bg-green-100 text-green-700"
            }`}
          >
            Save ${YEARLY_SAVINGS.toFixed(0)}
          </span>
        </button>
      </div>
    </div>
  )
}

export function PricingPreview() {
  const { user, isPremium } = useAuth()
  // Renamed off `interval` to avoid shadowing the global `window.setInterval`
  // that some lint setups (and human readers) flag as confusing.
  const [billingInterval, setBillingInterval] =
    useState<BillingInterval>("yearly")
  const [loading, setLoading] = useState(false)

  const isYearly = billingInterval === "yearly"
  const paymentTimingLabel = `Start with a ${PREMIUM_TRIAL_DAYS}-day trial. Cancel anytime.`
  const renewalPriceLabel = isYearly ? WEB_DISCOUNT.yearly.webDisplay : WEB_DISCOUNT.monthly.webDisplay

  // Both intervals lead with the trial ("Free / for N days") — it's the actual
  // offer ($0 today on either plan), so headlining it only on monthly made the
  // trial read as monthly-only. The yearly per-month anchor ($4.00/mo) lives in
  // the sub-line instead, keeping the value framing without contradicting the
  // trial headline; the "Save $X" toggle badge reinforces the yearly discount.
  const premiumPriceBlock = isYearly
    ? {
        big: "Free",
        suffix: `for ${PREMIUM_TRIAL_DAYS} days`,
        sub: `Then $${YEARLY_PRICE}/year ($${(YEARLY_PRICE / 12).toFixed(2)}/mo). Cancel anytime.`,
        appStoreAnchor: WEB_DISCOUNT.yearly.appStoreDisplay,
      }
    : {
        big: "Free",
        suffix: `for ${PREMIUM_TRIAL_DAYS} days`,
        sub: `Then $${MONTHLY_PRICE}/mo. Cancel anytime.`,
        appStoreAnchor: WEB_DISCOUNT.monthly.appStoreDisplay,
      }

  const premiumCtaLabel = `Start my ${PREMIUM_TRIAL_DAYS}-day trial`

  const handleFreeClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    // Deliberate Free-tier framing ("Try free analysis") preserved; the destination
    // fields come from the shared helper so this identify CTA is instrumented too.
    trackEvent(
      "cta_clicked",
      identifyCtaProps(IDENTIFY_CTA.location.pricingPreview, { text: "Try free analysis" }),
    )
    document
      .getElementById("analysis-card")
      ?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  const handlePremiumClick = async (event: MouseEvent<HTMLButtonElement>) => {
    if (loading) return

    const navigationTarget = openModifiedClickTarget(event)

    trackEvent("cta_clicked", {
      cta_location: "homepage_pricing_preview_premium",
      cta_text: premiumCtaLabel,
      billing_interval: billingInterval,
    })
    analytics.upgradeClicked("homepage_pricing_preview", billingInterval)

    setLoading(true)

    // Auth-aware: signed-in users get the regular checkout (links to their
    // existing Stripe customer); anonymous users get the guest endpoint
    // (Stripe collects email and we link via webhook after payment).
    const endpoint = user
      ? "/api/stripe/create-checkout"
      : "/api/stripe/create-guest-checkout"

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tier: billingInterval }),
      })
      const data = await response.json()

      if (data.url) {
        const amount = isYearly
          ? YEARLY_PRICE
          : MONTHLY_PRICE
        analytics.checkoutInitiated(billingInterval, amount)
        navigateToResolvedUrl(data.url, navigationTarget)
        return
      }

      // Already entitled is a refusal, not a failure: checkout declined to bill
      // them twice. Send them to /pricing without the error flag, where a
      // premium session renders the manage-billing path instead.
      if (data.code === "already_subscribed") {
        navigateToResolvedUrl("/pricing", navigationTarget)
        return
      }

      // Checkout creation failed — fall back to the pricing page so the user
      // still has a path forward. Keeps the homepage from dead-ending.
      navigateToResolvedUrl("/pricing?checkout=error", navigationTarget)
    } catch (error) {
      console.error("Pricing preview checkout error:", error)
      navigateToResolvedUrl("/pricing?checkout=error", navigationTarget)
    } finally {
      setLoading(false)
    }
  }

  return (
    <section className="bg-linear-to-b from-green-50/40 to-white px-7 py-12 md:py-16">
      <div className="max-w-[1100px] mx-auto">
        <Reveal>
          <TrustBand />
          <h2 className="text-center font-bold tracking-[-0.02em] text-gray-900 mb-2 text-[28px] md:text-4xl">
            Simple pricing
          </h2>
          <p className="text-center text-sm md:text-base text-gray-600 mb-6 md:mb-8">
            {paymentTimingLabel}
          </p>
        </Reveal>

        <BillingToggle
          interval={billingInterval}
          onChange={setBillingInterval}
        />

        <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-3xl mx-auto items-stretch">
          {/* Free tier — demoted: smaller, lighter border, neutral CTA */}
          <Stagger.Item>
            <HoverLift
              lift={-3}
              pressScale={1}
              className="relative rounded-2xl bg-white border border-gray-200 p-6 md:p-7 flex flex-col h-full"
            >
              <h3 className="text-lg font-bold text-gray-900">Free</h3>
              <p className="mt-1 text-xs text-gray-500">
                Try the basics, no card required
              </p>
              <div className="mt-3 flex items-baseline">
                <span className="text-4xl font-extrabold text-gray-900">$0</span>
                <span className="ml-1.5 text-sm font-medium text-gray-500">
                  forever
                </span>
              </div>
              {/* Reserve the same vertical space the Premium card uses for its
                  price sub-line so both card bodies start at the same Y. */}
              <div className="mt-1 min-h-[1rem]" aria-hidden="true" />

              <ul className="mt-5 space-y-2.5 flex-1">
                {FREE_FEATURES.map((feature) => (
                  <li
                    key={feature}
                    className="flex items-start gap-2 text-sm text-gray-700"
                  >
                    <Check className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <a
                href="#analysis-card"
                onClick={handleFreeClick}
                className="mt-6 inline-flex w-full items-center justify-center min-h-[48px] px-5 py-3 rounded-xl border border-gray-300 bg-white text-gray-900 font-semibold text-sm hover:bg-gray-50 hover:border-gray-400 transition-colors"
              >
                Try free analysis
              </a>
            </HoverLift>
          </Stagger.Item>

          {/* Premium tier — promoted: stronger border, popular badge, gradient CTA */}
          <Stagger.Item>
            <HoverLift
              lift={-3}
              pressScale={1}
              className="relative rounded-2xl bg-white border-2 border-green-500 shadow-[0_8px_32px_rgb(22,101,52,0.10)] p-6 md:p-7 flex flex-col h-full"
            >
              <PopularBadge />
              <h3 className="text-lg font-bold text-gray-900">Smart reminders timed to your zip &amp; grass</h3>
              <p className="mt-1 text-xs text-gray-500">
                Everything in Free, plus what actually saves your lawn
              </p>

              {/* Price block — morphs per billing interval. The big number is
                  the one we want users to anchor on (free trial on monthly,
                  per-month equivalent on yearly). The sub-line carries the
                  fine print so the headline never looks crowded. */}
              <div className="mt-3 flex items-baseline">
                <span className="text-4xl font-extrabold text-gray-900">
                  {premiumPriceBlock.big}
                </span>
                <span className="ml-1.5 text-sm font-medium text-gray-500">
                  {premiumPriceBlock.suffix}
                </span>
              </div>
              <div className="mt-1 text-xs text-gray-600 font-medium">
                {premiumPriceBlock.sub}
              </div>

              {/* Matches the /pricing card. See the note there on why the reason
                  line ships alongside the struck-through anchor. */}
              {WEB_DISCOUNT.active && (
                <div className="mt-2">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="text-xs font-medium text-gray-400 line-through">
                      {premiumPriceBlock.appStoreAnchor}
                    </span>
                    <span className="rounded-full bg-green-50 px-2 py-0.5 text-[11px] font-semibold text-green-700">
                      Save {WEB_DISCOUNT.label} buying here
                    </span>
                  </div>
                  <p className="mt-1 text-[11px] leading-snug text-gray-500">
                    {WEB_DISCOUNT_REASON}
                  </p>
                </div>
              )}

              <PremiumTrialTimeline
                trialDays={PREMIUM_TRIAL_DAYS}
                renewalPriceLabel={renewalPriceLabel}
                className="mt-5"
              />

              <ul className="mt-5 space-y-2 flex-1">
                {PREMIUM_FEATURES.map((feature) => (
                  <li
                    key={feature.text}
                    className={`flex items-start gap-2 text-sm ${
                      feature.highlight
                        ? "-mx-2 px-2 py-1 rounded-md bg-green-50/70"
                        : ""
                    }`}
                  >
                    <Check
                      className={`w-4 h-4 shrink-0 mt-0.5 ${
                        feature.highlight ? "text-green-700" : "text-green-600"
                      }`}
                    />
                    <span
                      className={
                        feature.highlight
                          ? "font-semibold text-gray-900"
                          : "text-gray-700"
                      }
                    >
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              {isPremium ? (
                <Link
                  href="/dashboard"
                  className="mt-6 inline-flex w-full items-center justify-center min-h-[48px] px-5 py-3 rounded-xl bg-linear-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold text-sm md:text-base shadow-md transition-[background,box-shadow,transform] duration-200 hover:scale-[1.01] active:scale-[0.99]"
                >
                  Go to dashboard
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={handlePremiumClick}
                  disabled={loading}
                  className="mt-6 inline-flex w-full items-center justify-center min-h-[48px] px-5 py-3 rounded-xl bg-linear-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold text-sm md:text-base shadow-md transition-[background,box-shadow,transform] duration-200 hover:scale-[1.01] active:scale-[0.99] disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Starting checkout...
                    </>
                  ) : (
                    premiumCtaLabel
                  )}
                </button>
              )}

              {/* Risk-reversal triplet — the single highest-leverage block on
                  the card per /pricing's converted layout. Stays inline so
                  users don't have to scroll or hover to find it. */}
              <div className="mt-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[11px] text-gray-600">
                <span className="inline-flex items-center gap-1">
                  <Shield
                    className="w-3 h-3 text-green-600"
                    aria-hidden="true"
                  />
                  7-day money back
                </span>
                <span aria-hidden="true" className="text-gray-300">
                  ·
                </span>
                <span className="inline-flex items-center gap-1">
                  <RotateCcw
                    className="w-3 h-3 text-green-600"
                    aria-hidden="true"
                  />
                  Cancel anytime
                </span>
                <span aria-hidden="true" className="text-gray-300">
                  ·
                </span>
                <span className="inline-flex items-center gap-1">
                  <CreditCard
                    className="w-3 h-3 text-green-600"
                    aria-hidden="true"
                  />
                  No charge for {PREMIUM_TRIAL_DAYS} days
                </span>
              </div>
            </HoverLift>
          </Stagger.Item>
        </Stagger>

        {/* Factual value bridge + compare-plans escape hatch. */}
        <Reveal>
          <div className="mt-8 md:mt-10 text-center">
            <p className="text-sm md:text-base text-gray-700 max-w-xl mx-auto leading-relaxed">
              <span className="font-semibold text-gray-900">
                Keep every lawn decision in one plan.
              </span>{" "}
              Premium connects your grass, ZIP code, diagnoses, and reminders across the season.
            </p>
            <Link
              href="/pricing"
              onClick={() =>
                trackEvent("cta_clicked", {
                  cta_location: "homepage_pricing_preview_compare",
                  cta_text: "Compare all features",
                  billing_interval: billingInterval,
                })
              }
              className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-green-700 hover:text-green-800 transition-colors min-h-[44px] px-2"
            >
              Compare all features
              <ArrowDown className="w-3.5 h-3.5 -rotate-90" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  )
}
